import { Component, ElementRef, EventEmitter, forwardRef, Input, OnDestroy,
  OnInit, Output, ViewChild, ViewEncapsulation, ViewContainerRef, AfterViewInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UtilsService } from '@library/utils/services/utils.service';
import { fromEvent, merge, Subscription } from 'rxjs';
import { map, repeat, switchMap, take, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.sass'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SliderComponent),
      multi: true
    }
  ]
})
export class SliderComponent implements ControlValueAccessor, OnInit, OnDestroy, AfterViewInit {

  /**
   * Min, max and step of the slider.
   */
  @Input() min = 0;
  @Input() max = 100;
  @Input() step = 1;

  @Input() styleClass: string;

  /**
   * Value of the slider and event emitter.
   */
  private value: number;
  @Output() valueChange = new EventEmitter<number>();

  // Display direction
  rightToLeft = false;

  /**
   * Value of the slider handle, in percent.
   */
  private sliderHandleValue: number;

  /**
   * Width of the slider handle.
   * Used to set the handle position appropriately.
   */
  private sliderHandleWidth: number;

  /**
   * Position of the start of the slider, in pixels.
   * Used to calculate slider handle position and slider range width.
   */
  private sliderTrackInit: number;

  /**
   * Width of the slider track, in pixels.
   * Used to calculate slider handle position and slider range width.
   */
  private sliderTrackWidth: number;

  /**
   * IMPORTANT: The static field for ViewChild is mandatory from Angular 8.
   * See doc: https://angular.io/guide/static-query-migration.
   * The "as any" is there to keep the applications working in Angular 7 and below.
   * Remove the "as any" when every application is migrated to Angular 8.
   */
  @ViewChild('sliderHandle', {static: true} as any) sliderHandle: ElementRef<HTMLSpanElement>;
  /**
   * Slider track: where the handle can move.
   */
  @ViewChild('sliderTrack', {static: true} as any) sliderTrack: ElementRef<HTMLSpanElement>;
  /**
   * Slider range: active part of the slider, determined by handle position.
   */
  @ViewChild('sliderRange', {static: true} as any) sliderRange: ElementRef<HTMLSpanElement>;

  /**
   * Subscription for the move of the slider handle events.
   * Can be mouse events (on web) or touch events (on mobile).
   */
  private sliderHandleMoveSubscription: Subscription;

  /**
   * Subscription for the click on the slider event.
   */
  private sliderClickSubscription: Subscription;

  propagateChange: any = () => {};

  propagateTouched: any = () => {};

  constructor(
    private ref: ViewContainerRef,
    private utilsService: UtilsService,
    ) {}

  ngOnInit() {
    const sliderTrackBounds = this.sliderTrack.nativeElement.getBoundingClientRect();
    const sliderHandleBounds = this.sliderHandle.nativeElement.getBoundingClientRect();

    this.sliderTrackInit = sliderTrackBounds.left;
    this.sliderTrackWidth = sliderTrackBounds.right - this.sliderTrackInit;
    this.sliderHandleWidth = sliderHandleBounds.right - sliderHandleBounds.left;

    const start$ = merge(
      fromEvent(this.sliderHandle.nativeElement, 'mousedown'),
      fromEvent<TouchEvent>(this.sliderHandle.nativeElement, 'touchstart').pipe(map(evt => evt.touches[0])),
    );

    const move$ = merge(
      fromEvent(window, 'mousemove'),
      fromEvent<TouchEvent>(window, 'touchmove').pipe(map(evt => evt.touches[0])),
    );

    const end$ = merge(
      fromEvent(window, 'mouseup'),
      fromEvent(window, 'touchend'),
    );

    this.sliderHandleMoveSubscription = start$.pipe(
      switchMap(() => move$.pipe(
        tap((event: MouseEvent | Touch) => {
          this.changeValue({
            ...event,
            clientX: this.rightToLeft
              ? document.body.getBoundingClientRect().right - event.clientX
              : event.clientX
          });
        }),
        takeUntil(end$),
      )),
      repeat()
    ).subscribe();

    this.sliderClickSubscription = fromEvent(this.sliderTrack.nativeElement, 'click').pipe(
      tap((event: MouseEvent) => {
        this.changeValue({
          ...event,
          clientX: this.rightToLeft
            ? document.body.getBoundingClientRect().right - event.clientX
            : event.clientX
        });
      }),
      take(1),
      repeat()
    ).subscribe();
  }

  ngAfterViewInit() {
    this.rightToLeft = getComputedStyle(this.ref.element.nativeElement).direction === 'rtl';
  }

  /**
   * Handles the user moving the slider handle or clicking on the slider track.
   * Updates the slider value, slider handle position and slider range width.
   */
  changeValue(event: MouseEvent | Touch): void {
    this.sliderHandleValue = this.calculateSliderHandleValue(event);
    this.value = this.calculateValueFromSliderHandle(this.sliderHandleValue);

    this.setSliderStyleProperties();

    this.valueChange.emit(this.value);
    this.propagateChange(this.value);
  }

  /**
   * Calculate the slider handle value, in percent.
   * Min = 0% and max = 100%.
   */
  calculateSliderHandleValue(event: MouseEvent | Touch): number {
    let cursorPosition = this.sliderTrackInit;

    // If the event is a mouse event (on web) or a click
    cursorPosition = event.clientX;

    const perc = ((cursorPosition - this.sliderTrackInit) * 100) / (this.sliderTrackWidth);
    let percRounded = this.utilsService.roundToNearestStep(perc, 0.01, 2);
    if (percRounded < 0) {
      percRounded = 0;
    } else if (percRounded > 100) {
      percRounded = 100;
    }
    return percRounded;
  }

  /**
   * Calculates the value of the slider according to the value of the slider handle.
   * @param sliderHandleValue Value of the slider handle.
   */
  calculateValueFromSliderHandle(sliderHandleValue: number): number {
    const convertedValue = (this.max - this.min) * (sliderHandleValue / 100) + this.min;
    let roundedValue = this.utilsService.roundToNearestStep(convertedValue, this.step);

    if (roundedValue < this.min) {
      roundedValue = this.min;
    } else if (roundedValue > this.max) {
      roundedValue = this.max;
    }

    return roundedValue;
  }

  setSliderStyleProperties() {
    const percent = this.utilsService.roundToNearestStep((this.value - this.min) * 100 / (this.max - this.min), 1);
    this.sliderRange.nativeElement.style.setProperty(
      'width', 'calc(' + percent + '%)'
    );
    const value = this.rightToLeft ? 100 - percent : percent;
    this.sliderHandle.nativeElement.style.setProperty(
      'left', 'calc(' + value + '% - ' + this.sliderHandleWidth / 2 + 'px)'
    );
  }

  writeValue(value: any): void {
    if (value !== undefined) {
      this.value = value;
    }

    this.writeHandleValue();
  }

  writeHandleValue(): void {
    if (this.value <= this.min) {
      this.sliderHandleValue = 0;
    } else if (this.value >= this.max) {
      this.sliderHandleValue = 100;
    } else {
      this.sliderHandleValue = this.utilsService.roundToNearestStep((this.value - this.min) * 100 / (this.max - this.min), 1);
    }

    this.setSliderStyleProperties();
  }

  registerOnChange(fn: () => void): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.propagateTouched = fn;
  }

  ngOnDestroy() {
    this.sliderClickSubscription.unsubscribe();
    this.sliderHandleMoveSubscription.unsubscribe();
  }
}

/*
 * Deprecated need to use range
 */
